<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router/composables";
import EvaluationScore from "@/components/CandidateSidebar/HistoryTab/components/EvaluationScore";
import { recommendationColor } from "@/common/humanly/fitScore";

const props = defineProps({
  candidacies: {
    type: Array,
    default: () => [],
  },
});

const router = useRouter();

const sortedCandidacies = computed(() =>
  [...props.candidacies].sort((a, b) => (b.aggregate_score ?? -1) - (a.aggregate_score ?? -1))
);

function jobHref(candidacy) {
  const crmJobId = candidacy.job?.crm_job_id;
  if (!crmJobId) return null;
  return router.resolve({ name: "job-root", params: { projectId: crmJobId } }).href;
}
</script>

<template>
  <div v-if="sortedCandidacies.length" class="job-fit">
    <h4>Job Fit</h4>

    <div v-for="candidacy in sortedCandidacies" :key="candidacy.id" class="job-fit-card">
      <div class="d-flex align-items-center justify-content-between mb-12">
        <div class="d-flex align-items-center">
          <div class="d-flex flex-column align-items-center">
            <evaluation-score :score="candidacy.aggregate_score" badge />
            <span class="fit-score-label text-color-black-50 mt-4">Fit Score</span>
          </div>
          <a
            v-if="jobHref(candidacy)"
            class="ml-16 font-weight-600 job-title-link"
            :href="jobHref(candidacy)"
          >
            {{ candidacy.job?.title }}
          </a>
          <div v-else class="ml-16 font-weight-600">{{ candidacy.job?.title }}</div>
        </div>

        <span
          v-if="candidacy.recommendation"
          class="recommendation-pill"
          :class="recommendationColor(candidacy.recommendation)"
        >
          {{ candidacy.recommendation }}
        </span>
      </div>

      <div v-if="candidacy.summary" class="summary-container">
        {{ candidacy.summary }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.job-fit {
  margin-bottom: 16px;
}

.job-fit-card {
  background-color: $black-5;
  padding: 12px;
  border-radius: $tf-global-border-radius;
  margin-bottom: 12px;
}

.fit-score-label {
  font-size: 11px;
  font-weight: 500;
}

.summary-container {
  white-space: pre-wrap;
  line-height: 1.5;
}

.job-title-link {
  color: $blue-100;

  &:hover {
    text-decoration: underline;
  }
}

.recommendation-pill {
  color: #fff;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
  font-size: 12px;

  &.green {
    background-color: $tf-color-success;
  }

  &.yellow {
    background-color: $tf-color-warning;
  }

  &.red {
    background-color: $tf-color-danger;
  }
}
</style>
